/** Booking page API (owner request, 15 October 2026): no session until the patient books. */
import type {
  BookingPageOptionsResponse,
  BookingPageRequest,
  BookingPageResponse,
  BookingStatusResponse,
  HandoffRequest,
  WidgetEvent,
  HandoffResponse,
  SlotsResponse,
} from "@wapadesk/schema";
import { ApiClientError } from "./api";
import { withEmbed } from "./embed";

export class BookApi {
  token: string | undefined;
  constructor(private readonly origin: string) {}

  protected async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { "content-type": "application/json" };
    if (this.token) headers["authorization"] = `Bearer ${this.token}`;
    const res = await fetch(`${this.origin}${withEmbed(path)}`, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      credentials: "omit",
      mode: "cors",
    });
    const json = (await res.json().catch(() => ({}))) as { error?: string; details?: unknown };
    if (!res.ok) throw new ApiClientError(res.status, json.error ?? "http_error", json.details);
    return json as T;
  }

  /** A share link carries the slug, the loader the clinic id; either works. */
  async options(ref: string): Promise<BookingPageOptionsResponse> {
    if (/^[a-z0-9-]{2,64}$/.test(ref)) {
      try {
        return await this.call("GET", `/v1/widget/book/options?slug=${encodeURIComponent(ref)}`);
      } catch (e) {
        if (!(e instanceof ApiClientError) || e.status !== 404) throw e;
      }
    }
    return this.call("GET", `/v1/widget/book/options?clinic=${encodeURIComponent(ref)}`);
  }
  slots(q: {
    clinic: string;
    type: string;
    doctor?: string | undefined;
    location?: string | undefined;
    from: string;
    days: number;
  }): Promise<SlotsResponse> {
    const p = new URLSearchParams({
      clinic: q.clinic,
      type: q.type,
      from: q.from,
      days: String(q.days),
    });
    if (q.doctor) p.set("doctor", q.doctor);
    if (q.location) p.set("location", q.location);
    return this.call("GET", `/v1/widget/book/slots?${p.toString()}`);
  }
  async book(body: BookingPageRequest): Promise<BookingPageResponse> {
    const r = await this.call<BookingPageResponse>("POST", "/v1/widget/book", body);
    this.token = r.sessionToken;
    return r;
  }
  status(appointmentId: string): Promise<BookingStatusResponse> {
    return this.call(
      "GET",
      `/v1/widget/booking/status?appointment=${encodeURIComponent(appointmentId)}`,
    );
  }
  /** After booking: the WhatsApp message with the appointment, or the same text by email. */
  handoff(body: HandoffRequest): Promise<HandoffResponse> {
    return this.call("POST", "/v1/widget/leads/current/handoff", body);
  }
  /** Analytics (9.4): ids, types and timings only. */
  async events(events: WidgetEvent[]): Promise<void> {
    await this.call("POST", "/v1/widget/events", { events });
  }
}
